/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import { AdobeData, AdobeDataLayer, AdobeDataLayerEvent } from './types';
import EventQueue from './EventQueue';

const scriptSrc = 'https://assets.adobedtm.com/ccc66c06b30b/a94aa5544266/launch-0874c9b2b355.min.js';

type Satellite = {
  pageBottom: () => void;
};

type CookieAccept = {
  clickListener: (prop: number) => void;
};

type Truste = {
  eu: CookieAccept;
};

interface AdobeWindow extends Window {
  adobeDataLayer: AdobeDataLayer;
  _satellite: Satellite;
  truste: Truste;
}

const isAdobeWindow = (win: Window): win is AdobeWindow =>
  'adobeDataLayer' in win && typeof (win as AdobeWindow).adobeDataLayer === 'object';

let script: HTMLScriptElement | undefined;

const queue = new EventQueue();

export function getAdobeDataLayer(): AdobeDataLayer {
  if (typeof window !== 'object') {
    return queue;
  }

  if (!isAdobeWindow(window)) {
    return queue;
  }

  // It seems that when user does not give consent to tracking
  // AdobeDataLayer is piling up the events and it can cause memory leak
  if (window.adobeDataLayer.length > 1_000) {
    window.adobeDataLayer.length = 0;
  }

  return window.adobeDataLayer;
}

export function getGlobalTracker() {
  const layer = getAdobeDataLayer();

  layer.push({
    event: AdobeDataLayerEvent.GLOBAL_DATA_LAYER,
    site: {
      name: 'bld-app',
    },
    user: {
      loginStatus: 'yes',
    },
  });

  return layer;
}

export function initialize(enabled = false) {
  if (!enabled) {
    queue.convertToSink();
    return;
  }
  if (!script && typeof document !== 'undefined') {
    script = document.createElement('script');
    script.src = scriptSrc;

    script.onload = () => {
      try {
        if (isAdobeWindow(window)) {
          getGlobalTracker();
          // eslint-disable-next-line no-underscore-dangle
          window._satellite.pageBottom();

          // Send initial events that were waiting for initialize
          setTimeout(() => {
            const layer = getAdobeDataLayer();
            while (queue.length > 0) {
              layer.push(queue.shift() as AdobeData);
            }
            queue.convertToSink();
          }, 100);
        }
      } catch (e) {
        console.error(e);
      }
    };

    script.onerror = (e) => {
      queue.convertToSink();
      Sentry.captureException('Adobe analytics script could not be loaded', { originalException: e });
      console.error('failed to load analytics script');
    };

    document.head.append(script);
  }
}
